import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ASSISTANT_RESPONSE_LANGUAGE_OPTIONS,
  DEFAULT_ASSISTANT_RESPONSE_LANGUAGE,
} from "@/lib/assistantLanguage";
import {
  DEFAULT_MESSAGE_SPLIT_STYLE,
  MESSAGE_SPLIT_STYLE_OPTIONS,
} from "@/lib/messageSplitStyle";
import {
  AI_MODEL_OPTIONS,
  isLegacyModel,
  type AiModelCostTier,
} from "@/lib/aiModels";

interface ConfigureTabProps {
  formData: any;
  onFormChange: (field: string, value: any) => void;
}

const fieldClass =
  "h-11 !h-[44px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]";

const COST_TIER_STYLES: Record<
  AiModelCostTier,
  { labelKey: string; badge: string; dot: string }
> = {
  low: {
    labelKey: "configure.costTierLow",
    badge:
      "border-[#BBF7D0] bg-[#F0FDF4] text-[#15803D] dark:border-[#166534] dark:bg-[#052E16] dark:text-[#4ADE80]",
    dot: "bg-[#22C55E]",
  },
  medium: {
    labelKey: "configure.costTierMedium",
    badge:
      "border-[#FDE68A] bg-[#FFFBEB] text-[#B45309] dark:border-[#854D0E] dark:bg-[#2A1F04] dark:text-[#FBBF24]",
    dot: "bg-[#F59E0B]",
  },
  high: {
    labelKey: "configure.costTierHigh",
    badge:
      "border-[#FECDD3] bg-[#FFF1F2] text-[#BE123C] dark:border-[#9F1239] dark:bg-[#3B0713] dark:text-[#FB7185]",
    dot: "bg-[#F43F5E]",
  },
};

const costBadgeBaseClass =
  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium";

// Inherits the surrounding text color, so it reads correctly in both themes without a variant.
const OpenAiMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
  </svg>
);

export const TimeToRespondSecondsOptions = [
  "1",
  "2",
  "3",
  "5",
  "10",
  "15",
  "20",
  "30",
  "45",
  "60",
];

export const MaximumMessagesOptions = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "10",
  "30",
  "60",
];

const ConfigureTab: React.FC<ConfigureTabProps> = ({ formData, onFormChange }) => {
  const { t } = useTranslation("assistants");
  const handleTimeValueChange = (value: string) => {
    onFormChange("response_time_value", parseInt(value, 10) || 10);
    onFormChange("response_time_unit", "seconds");
  };

  const modelValue =
    typeof formData.model === "string" && formData.model.trim()
      ? formData.model.trim()
      : undefined;

  // Assistants created before the official model list keep the model they were built on: the
  // contact's OpenAI response chain was produced by it, and moving a live chain to another model
  // is not supported. To use a new model the user creates a new assistant.
  const modelLocked = isLegacyModel(modelValue);

  const timeValue =
    formData.response_time_value !== null &&
    formData.response_time_value !== undefined &&
    formData.response_time_value !== ""
      ? String(formData.response_time_value)
      : undefined;

  const timeOptions =
    timeValue && !TimeToRespondSecondsOptions.includes(timeValue)
      ? [...TimeToRespondSecondsOptions, timeValue]
      : TimeToRespondSecondsOptions;

  return (
    <form action="" className="w-full flex pb-8 flex-col gap-5 p-1 md:p-2">
      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="name" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("configure.assistantName")}</Label>
        <Input
          placeholder={t("configure.namePlaceholder")}
          id="name"
          value={formData.name || ""}
          onChange={(e) => onFormChange("name", e.target.value)}
          className={`${fieldClass} !w-full text-[14px]`}
          type="text"
        />
      </div>
      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="model" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("configure.model")}</Label>
        {modelLocked ? (
          <>
            <div
              className={`${fieldClass} flex w-full items-center gap-2 px-3 text-[14px] opacity-70`}
            >
              <OpenAiMark className="h-4 w-4 shrink-0" />
              {modelValue}
            </div>
            <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">
              {t("configure.modelLocked")}
            </p>
          </>
        ) : (
          <Select
            key={`model-${modelValue ?? "empty"}`}
            value={modelValue}
            onValueChange={(value) => onFormChange("model", value)}
          >
            <SelectTrigger className={`w-full ${fieldClass}`}>
              <SelectValue placeholder={t("configure.pickModel")} />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#E5E7EB] dark:bg-[#27272A] dark:border-[#3F3F46]">
              <SelectGroup>
                {AI_MODEL_OPTIONS.map((item) => {
                  const tier = COST_TIER_STYLES[item.costTier];
                  return (
                    <SelectItem key={item.value} value={item.value}>
                      <span className="flex items-center gap-2">
                        <OpenAiMark className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                        <span className={`${costBadgeBaseClass} ${tier.badge}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${tier.dot}`} />
                          {t(tier.labelKey)}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="response-language" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">
          {t("configure.responseLanguage")}
        </Label>
        <Select
          value={formData.response_language || DEFAULT_ASSISTANT_RESPONSE_LANGUAGE}
          onValueChange={(value) => onFormChange("response_language", value)}
        >
          <SelectTrigger id="response-language" className={`w-full ${fieldClass}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#E5E7EB] dark:bg-[#27272A] dark:border-[#3F3F46]">
            <SelectGroup>
              {ASSISTANT_RESPONSE_LANGUAGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">
          {t("configure.responseLanguageHint")}
        </p>
      </div>
      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="message-split-style" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">
          {t("configure.splitStyle")}
        </Label>
        <Select
          value={formData.message_split_style || DEFAULT_MESSAGE_SPLIT_STYLE}
          onValueChange={(value) => onFormChange("message_split_style", value)}
        >
          <SelectTrigger id="message-split-style" className={`w-full ${fieldClass}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#E5E7EB] dark:bg-[#27272A] dark:border-[#3F3F46]">
            <SelectGroup>
              {MESSAGE_SPLIT_STYLE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {t(item.labelKey)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">
          {t(
            (formData.message_split_style || DEFAULT_MESSAGE_SPLIT_STYLE) === "single"
              ? "configure.splitStyleHintSingle"
              : "configure.splitStyleHintMulti",
          )}
        </p>
      </div>
      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="time-to-respond" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("configure.responseTime")}</Label>
        <div className="flex items-center gap-3">
          <Select
            key={`time-${timeValue ?? "empty"}`}
            value={timeValue}
            onValueChange={handleTimeValueChange}
          >
            <SelectTrigger className={`w-24 ${fieldClass}`}>
              <SelectValue placeholder={t("common.pick")} />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#E5E7EB] dark:bg-[#27272A] dark:border-[#3F3F46]">
              <SelectGroup>
                {timeOptions.map((item, index) => (
                  <SelectItem key={`time-${index}-${item}`} value={item}>
                    {item}s
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-[#64748B] dark:text-[#71717A]">
          {t("configure.responseTimeHint")}
        </p>
      </div>
      <div className="flex w-full flex-col items-start gap-2 pt-1">
        <div className="flex w-full items-start gap-3">
          <Switch
            id="disable-assistant-on-manual-message"
            className="mt-0.5 shrink-0"
            checked={formData.disable_assistant_on_manual_message ?? false}
            onCheckedChange={(checked) =>
              onFormChange("disable_assistant_on_manual_message", checked)
            }
          />
          <div className="flex flex-col gap-1 min-w-0">
            <Label
              htmlFor="disable-assistant-on-manual-message"
              className="text-sm font-medium text-[#0F172A] leading-snug cursor-pointer dark:text-[#FAFAFA]"
            >
              {t("configure.disableOnManual")}
            </Label>
            <p className="text-[11px] leading-relaxed text-[#64748B] dark:text-[#71717A]">
              {t("configure.disableOnManualHint")}
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ConfigureTab;
